import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, Users, AlertCircle, ChevronLeft, ChevronRight, RotateCcw, Type } from "lucide-react";
import { api, type SpotlightTherapist } from "../../lib/api.js";
import { SpotlightEditor, CAMPAIGN_STATUS_HE } from "./SpotlightEditor.js";

/**
 * Every spotlight campaign, one click from its therapist.
 *
 * A campaign in progress was only reachable by finding its therapist again in a
 * roster of hundreds. This is the list of people we are mid-conversation with.
 */
function CampaignStrip({ onPick }: { onPick: (slug: string) => void }) {
  const q = useQuery({ queryKey: ["spotlight", "campaigns"], queryFn: () => api.listSpotlightCampaigns() });
  const items = q.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-line bg-canvas px-3 py-2 text-2xs">
      <span className="font-medium text-ink">קמפיינים:</span>
      {items.slice(0, 30).map((c) => {
        // Asked, and no answer for three days. Silence is not consent, so the
        // strip says so instead of letting the campaign sit.
        const waiting = c.status === "sent" && !!c.sentAt && Date.now() - Date.parse(c.sentAt) > 3 * 864e5;
        return (
          <button
            key={c.id}
            onClick={() => onPick(c.therapistSlug)}
            className={`rounded-full border px-2 py-0.5 hover:bg-canvas ${waiting ? "border-amber-400 bg-amber-50" : "border-line bg-white"}`}
            title={`עודכן ${new Date(c.updatedAt).toLocaleString("he-IL")}`}
          >
            <span dir="rtl">{c.therapistName}</span> · {CAMPAIGN_STATUS_HE[c.status] ?? c.status}
            {waiting && <span className="text-amber-800"> · 3+ ימים בלי תשובה</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * "Meet the therapist" templates.
 *
 * Every card is an <iframe> pointing at `/api/spotlight/slide`, which returns the
 * same standalone document the PNG exporter renders. That is deliberate: a React
 * re-implementation of the card would drift from what actually gets published,
 * and the drift would only surface after a post was live.
 */

const SLIDE_LABELS: Record<string, string> = {
  cover: "שער", methods: "טיפולים", where: "איפה ואיך",
  quote: "ציטוט", reviews: "ביקורות", cta: "צרו קשר",
};

/**
 * Reorderable slides. `cta` is NOT in this list.
 *
 * The closing card is the only one with a job that depends on position: it is
 * the instruction the reader leaves with. Putting it anywhere but last would
 * ask for the action before showing what the action is about, so it is pinned
 * rather than merely defaulted to the end.
 */
const REORDERABLE = ["cover", "methods", "where", "quote", "reviews"] as const;
const PINNED_LAST = "cta";

const FORMATS: { key: string; label: string; w: number; h: number }[] = [
  { key: "square", label: "ריבוע 1:1", w: 1080, h: 1080 },
  { key: "portrait", label: "פורטרט 4:5", w: 1080, h: 1350 },
  { key: "story", label: "סטורי 9:16", w: 1080, h: 1920 },
];

const THEMES: { key: string; label: string; swatch: string }[] = [
  { key: "lavender", label: "לבנדר", swatch: "#EDE4F6" },
  { key: "sage", label: "מרווה", swatch: "#E3F0E8" },
  { key: "plum", label: "סגול", swatch: "#8B67A2" },
  { key: "ink", label: "דיו", swatch: "#221a33" },
];

/**
 * Size multipliers the operator can nudge, per component.
 *
 * Multipliers, not pixel values: the same setting has to survive a switch from
 * 1:1 to 9:16, and a px size tuned on a square is wrong on a story.
 */
const SIZE_CONTROLS: { key: string; label: string; hint: string }[] = [
  { key: "name", label: "שם", hint: "שם המטפל/ת בשקופית השער" },
  { key: "role", label: "כותרת", hint: "שורת התפקיד מתחת לשם" },
  { key: "kicker", label: "כותרת סקשן", hint: "״הכירו את״, ״איפה ואיך״" },
  { key: "chips", label: "תגיות", hint: "שיטות ומאפייני שירות" },
  { key: "facts", label: "פרטים", hint: "ערכים בשקופית ״איפה ואיך״" },
  { key: "quote", label: "ציטוט", hint: "גוף הציטוט" },
  { key: "logo", label: "לוגו", hint: "גודל סמל אלטרנבי" },
];
const DEFAULT_SCALE: Record<string, number> = Object.fromEntries(SIZE_CONTROLS.map((c) => [c.key, 1]));

const KIND_HE: Record<string, string> = {
  therapist: "מטפל/ת", teacher: "מורה / מדריכ/ה", coach: "מאמנ/ת", doula: "דולה", mixed: "משולב",
};

export function SocialTemplatesTab() {
  const [slug, setSlug] = useState<string | null>(null);
  const [format, setFormat] = useState("square");
  const [theme, setTheme] = useState("lavender");
  const [safe, setSafe] = useState(false);
  const [q, setQ] = useState("");
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [order, setOrder] = useState<string[]>([...REORDERABLE]);
  const [scale, setScale] = useState<Record<string, number>>({ ...DEFAULT_SCALE });
  const [sizesOpen, setSizesOpen] = useState(false);
  // Bumped whenever the campaign changes, to force the iframes to re-fetch.
  const [nonce, setNonce] = useState(0);

  const listQuery = useQuery({ queryKey: ["spotlight", "therapists"], queryFn: () => api.listSpotlightTherapists() });
  const items = listQuery.data?.items ?? [];
  const selected = useMemo(
    () => items.find((i) => i.slug === slug) ?? items[0] ?? null,
    [items, slug],
  );
  // Start with the therapists who can carry the post to their own followers:
  // a spotlight for someone with no feed is still a good post, it just does not
  // compound. Off by default so the full roster stays the default view.
  const [reshareOnly, setReshareOnly] = useState(false);
  const filtered = useMemo(() => {
    const needle = q.trim();
    return items.filter((i) => {
      if (reshareOnly && !i.reshareReach) return false;
      if (!needle) return true;
      return i.name.includes(needle) || i.cities.some((c) => c.includes(needle));
    });
  }, [items, q, reshareOnly]);

  // Switching therapist abandons the current campaign context.
  const pick = (s: string) => { setSlug(s); setCampaignId(null); setNonce((n) => n + 1); };
  const onCampaign = (id: string | null) => { setCampaignId(id); setNonce((n) => n + 1); };

  const exportQuery = useQuery({
    queryKey: ["spotlight", "payload", slug ?? items[0]?.slug, campaignId, nonce],
    queryFn: () => api.getSpotlightPayload((slug ?? items[0]!.slug)!, campaignId),
    enabled: items.length > 0,
  });
  const dropped = new Map((exportQuery.data?.dropped ?? []).map((d) => [d.slide, d.reason]));

  const move = (key: string, dir: -1 | 1) => {
    setOrder((prev) => {
      const i = prev.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  };
  const slides = [...order, PINNED_LAST];

  const fmt = FORMATS.find((f) => f.key === format)!;
  // Preview width is fixed; height follows the real aspect ratio so a story
  // preview is actually tall rather than a squashed square.
  const previewW = 268;
  const previewH = Math.round((fmt.h / fmt.w) * previewW);

  return (
    <div className="space-y-5">
      <CampaignStrip onPick={(s) => pick(s)} />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="max-w-3xl text-sm text-muted">
          כרטיסי "הכירו את המטפלים". כל שקופית נטענת מ-<code className="rounded bg-line px-1 py-0.5 font-mono text-xs">/api/spotlight/slide</code> —
          אותו מסמך שהייצוא ל-PNG מרנדר, כדי שמה שאתה רואה כאן הוא בדיוק מה שיתפרסם.
          הכותרת נגזרת מסוג הספק, כך שמורה יוגה לא מקבל/ת "הטיפולים של".
        </p>
        {listQuery.data && (
          <div className="flex shrink-0 items-center gap-2 text-2xs">
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800">{listQuery.data.eligibleCount} כשירים</span>
            <span className="rounded-full bg-canvas px-2 py-0.5 text-muted">{listQuery.data.blockedCount} חסומים</span>
            {listQuery.data.socialsVisible
              ? <span className="rounded-full bg-sky-50 px-2 py-0.5 text-sky-800" title="כשירים שיש להם פייסבוק, אינסטגרם או טיקטוק — הם יכולים לשתף את הפוסט לקהל שלהם">
                  {listQuery.data.reshareCount} עם רשתות
                </span>
              : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-900" title="הרוסטר נמשך בלי THERAPISTS_API_TOKEN, אז שדה הרשתות לא חוזר כלל. זה לא אומר שאין להם — אנחנו פשוט לא רואים.">
                  רשתות לא זמינות
                </span>}
            {Object.entries(listQuery.data.byKind).map(([k, n]) => (
              <span key={k} className="rounded-full bg-canvas px-2 py-0.5 text-muted">{KIND_HE[k] ?? k}: {n}</span>
            ))}
          </div>
        )}
      </div>

      {listQuery.isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-line bg-white p-6 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> טוען את הרוסטר מהאתר…
        </div>
      )}
      {listQuery.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">לא ניתן לטעון את רשימת המטפלים</div>
            <div className="mt-0.5 text-xs">{(listQuery.error as Error).message}</div>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
          {/* picker */}
          <div className="space-y-2">
            <div className="relative">
              <Search className="pointer-events-none absolute right-2.5 top-2.5 h-3.5 w-3.5 text-muted" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="חיפוש שם או עיר…"
                className="w-full rounded-lg border border-line bg-white py-2 pr-8 pl-3 text-sm"
              />
            </div>
            {listQuery.data?.socialsVisible && (
              <label className="flex items-center gap-1.5 px-1 text-2xs text-muted" title="פייסבוק, אינסטגרם או טיקטוק. אתר או וואטסאפ אינם פיד — אי אפשר לשתף אליהם פוסט.">
                <input type="checkbox" checked={reshareOnly} onChange={(e) => setReshareOnly(e.target.checked)} />
                רק מי שיכול לשתף אצלו
              </label>
            )}
            <div className="max-h-[560px] space-y-1 overflow-y-auto rounded-lg border border-line bg-white p-1.5">
              {filtered.map((t) => (
                <TherapistRow key={t.slug} t={t} active={selected?.slug === t.slug} onPick={() => pick(t.slug)} />
              ))}
              {filtered.length === 0 && <div className="p-3 text-center text-xs text-muted">אין התאמה</div>}
            </div>
          </div>

          {/* gallery */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Seg label="פורמט" options={FORMATS.map((f) => ({ key: f.key, label: f.label }))} value={format} onChange={setFormat} />
              <Seg label="רקע" options={THEMES.map((t) => ({ key: t.key, label: t.label, swatch: t.swatch }))} value={theme} onChange={setTheme} />
              <label className="ml-auto flex items-center gap-1.5 text-2xs text-muted" title="מסמן את השוליים הבטוחים. תצוגה בלבד, לא נכנס לייצוא.">
                <input type="checkbox" checked={safe} onChange={(e) => setSafe(e.target.checked)} /> שולי בטיחות
              </label>
              <button onClick={() => setSizesOpen((v) => !v)} className="flex items-center gap-1 rounded border border-line px-2 py-1 text-2xs text-muted hover:bg-canvas">
                <Type className="h-3 w-3" /> גדלים {sizesOpen ? "▾" : "▸"}
              </button>
              <button onClick={() => { setOrder([...REORDERABLE]); setScale({ ...DEFAULT_SCALE }); }} className="flex items-center gap-1 rounded border border-line px-2 py-1 text-2xs text-muted hover:bg-canvas" title="החזר סדר וגדלים לברירת המחדל">
                <RotateCcw className="h-3 w-3" /> אפס
              </button>
            </div>

            {sizesOpen && (
              <div className="grid gap-x-5 gap-y-2 rounded-lg border border-line bg-canvas p-3 sm:grid-cols-2 lg:grid-cols-3">
                {SIZE_CONTROLS.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-2xs" title={c.hint}>
                    <span className="w-16 shrink-0 text-muted">{c.label}</span>
                    <input
                      type="range" min={0.6} max={1.6} step={0.05}
                      value={scale[c.key] ?? 1}
                      onChange={(e) => setScale((p) => ({ ...p, [c.key]: Number(e.target.value) }))}
                      className="flex-1"
                    />
                    <span className="w-9 shrink-0 text-right font-mono text-muted">{(scale[c.key] ?? 1).toFixed(2)}</span>
                  </label>
                ))}
              </div>
            )}

            {selected && (
              <>
                <div className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span dir="rtl" className="font-semibold">{selected.name}</span>
                  <span className="text-2xs text-muted">{KIND_HE[selected.kind] ?? selected.kind}</span>
                  <span className="text-2xs text-muted">· ציון {selected.score}</span>
                  <span className="text-2xs text-muted">· {selected.cities.join(" · ")}</span>
                  <span className="text-2xs text-muted">· {selected.methodCount} שיטות</span>
                </div>
                {/* Keyed by therapist: the editor holds a campaign id, and without
                    the key it carried one therapist's campaign onto the next. */}
                <SpotlightEditor
                  key={selected.slug}
                  slug={selected.slug} name={selected.name} onCampaign={onCampaign}
                  format={format} theme={theme} order={slides} scale={scale}
                />

                <div className="flex flex-wrap gap-4">
                  {slides.map((key, idx) => (
                    <div key={key}>
                      <div className="mb-1 flex items-center gap-1">
                        <span className="font-mono text-[11px] text-muted">{idx + 1} · {SLIDE_LABELS[key] ?? key}</span>
                        {dropped.has(key) && (
                          <span className="rounded bg-amber-50 px-1 text-[10px] text-amber-900" title={`לא ייכנס לייצוא: ${dropped.get(key)}`}>
                            לא בייצוא
                          </span>
                        )}
                        {key === PINNED_LAST ? (
                          <span className="rounded bg-canvas px-1 text-[10px] text-muted" title="שקופית הסיום נעולה בסוף — היא הקריאה לפעולה.">נעול בסוף</span>
                        ) : (
                          <span className="ml-auto flex items-center gap-0.5">
                            <button onClick={() => move(key, -1)} disabled={idx === 0}
                              className="rounded border border-line p-0.5 text-muted hover:bg-canvas disabled:opacity-30" title="הזז אחורה">
                              <ChevronRight className="h-3 w-3" />
                            </button>
                            <button onClick={() => move(key, 1)} disabled={idx === order.length - 1}
                              className="rounded border border-line p-0.5 text-muted hover:bg-canvas disabled:opacity-30" title="הזז קדימה">
                              <ChevronLeft className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                      </div>
                      <div className={`overflow-hidden rounded-xl border bg-white shadow-sm ${dropped.has(key) ? "border-amber-300 opacity-55" : "border-line"}`} style={{ width: previewW, height: previewH }}>
                        <iframe
                          title={`${selected.name} — ${SLIDE_LABELS[key] ?? key}`}
                          src={`${api.spotlightSlideUrl({ slug: selected.slug, slide: key, format, theme, safe, campaign: campaignId, scale })}&n=${nonce}`}
                          // The document is 1080 wide; scale it down rather than
                          // letting the iframe reflow, so the preview is a true
                          // miniature of the export.
                          style={{ width: fmt.w, height: fmt.h, border: 0, transform: `scale(${previewW / fmt.w})`, transformOrigin: "top left" }}
                          scrolling="no"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One glyph per platform. Deliberately not brand logos: a row is 8px of space
 * and the point is "does this person have a feed", not which one.
 */
const SOCIAL_MARK: Record<string, string> = {
  facebook: "f", instagram: "◎", tiktok: "♪", youtube: "▶",
  linkedin: "in", x: "✕", whatsapp: "☎", website: "⌂", other: "•",
};

function TherapistRow({ t, active, onPick }: { t: SpotlightTherapist; active: boolean; onPick: () => void }) {
  return (
    <button
      onClick={onPick}
      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-right ${active ? "bg-brand-50 ring-1 ring-brand-200" : "hover:bg-canvas"}`}
    >
      {t.photoUrl
        ? <img src={t.photoUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
        : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-line"><Users className="h-3.5 w-3.5 text-muted" /></span>}
      <span className="min-w-0 flex-1">
        <span dir="rtl" className="block truncate text-xs font-medium">{t.name}</span>
        <span className="block truncate text-[10px] text-muted">{KIND_HE[t.kind] ?? t.kind} · {t.cities[0] ?? "—"}</span>
      </span>
      {t.socials.length > 0 && (
        <span className="shrink-0 text-[10px] text-sky-700" title={`רשתות: ${t.socials.join(", ")}`}>
          {t.socials.map((p) => SOCIAL_MARK[p] ?? "•").join("")}
        </span>
      )}
      <span className="shrink-0 font-mono text-[10px] text-muted">{t.score}</span>
    </button>
  );
}

function Seg({ label, options, value, onChange }: {
  label: string;
  options: { key: string; label: string; swatch?: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-2xs text-muted">{label}</span>
      <div className="flex overflow-hidden rounded-lg border border-line">
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-2xs ${value === o.key ? "bg-brand-50 font-medium text-brand-800" : "bg-white text-muted hover:bg-canvas"}`}
          >
            {o.swatch && <span className="h-2.5 w-2.5 rounded-full border border-line" style={{ background: o.swatch }} />}
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
